// Whole-database "possible duplicate patients" scan, for the one-off report
// script functions/findDuplicatePatients.mjs.
//
// It uses ONLY the portal's one same-patient rule (patientDuplicateMatch.js):
//   1. Same MRN (leading zeros ignored).
//   2. Same DOB + the same person's name (nicknames, middle names, typos...).
//   3. Same DOB + an MRN one digit off.
// Every pair that meets a rule is linked, and linked records form a group
// (A~B and B~C put A, B and C in one group), so each person appears once.
//
// Firebase-free and side-effect-free; explicit ".js" imports so the plain-Node
// script can import it as well as Jest.

import {
  mrnKey,
  normalizeDob,
  compareNames,
  mrnOneDigitApart,
} from "./patientDuplicateMatch.js";
import { confidenceFromNameMatch } from "./enrollmentMatch.js";

const nameOf = (r) => (r && (r.fullName || r.name || r.patientName)) || "";

// How strong a link is, for sorting groups strongest first.
const SAME_MRN_RANK = 200;
const rankOf = (link) => (link.rule === "same-mrn" ? SAME_MRN_RANK : link.namePercent || 50);

export const describeLink = (link) => {
  if (link.rule === "same-mrn") return "Same MRN";
  const parts = [];
  if (link.namePercent) parts.push(`Same DOB + name ${link.namePercent}%`);
  if (link.mrnOneOff) parts.push("Same DOB + MRN one digit off");
  return parts.join(", ");
};

/**
 * @param {Array<{id:string, source?:string, name?:string, fullName?:string, dob?:string, mrn?:string}>} records
 * @param {{ dismissed?: Set<string> }} [options] - pair keys (pairKeyOf) staff
 *   marked "Not a duplicate"
 * @returns {{
 *   groups: Array<{ records: Array<{record, label:string, why:string[]}>, links: Array, best:number }>,
 *   hiddenGroups: Array,     // the same shape, built from the dismissed pairs only
 *   unreadableDob: number,   // DOB present but not readable: only checked by MRN
 *   blankDob: number,        // no DOB: only checked by MRN
 * }}
 */
export function findDuplicateGroups(records, { dismissed } = {}) {
  const list = (records || []).filter(Boolean);
  const links = [];
  const linked = new Set();
  const pairKey = (i, j) => `${Math.min(i, j)}:${Math.max(i, j)}`;

  // Rule 1: bucket by MRN.
  const byMrn = new Map();
  list.forEach((r, i) => {
    const k = mrnKey(r.mrn);
    if (!k) return;
    if (!byMrn.has(k)) byMrn.set(k, []);
    byMrn.get(k).push(i);
  });
  for (const idx of byMrn.values()) {
    for (let a = 0; a < idx.length; a++) {
      for (let b = a + 1; b < idx.length; b++) {
        links.push({ a: idx[a], b: idx[b], rule: "same-mrn" });
        linked.add(pairKey(idx[a], idx[b]));
      }
    }
  }

  // Rules 2 and 3 both need the same DOB, so only compare within a DOB bucket.
  let unreadableDob = 0;
  let blankDob = 0;
  const byDob = new Map();
  list.forEach((r, i) => {
    const raw = String(r.dob || "").trim();
    const k = normalizeDob(raw);
    if (!k) {
      if (raw) unreadableDob++;
      else blankDob++;
      return;
    }
    if (!byDob.has(k)) byDob.set(k, []);
    byDob.get(k).push(i);
  });
  for (const idx of byDob.values()) {
    for (let a = 0; a < idx.length; a++) {
      for (let b = a + 1; b < idx.length; b++) {
        const i = idx[a];
        const j = idx[b];
        if (linked.has(pairKey(i, j))) continue; // already a same-MRN pair
        const nameMatch = compareNames(nameOf(list[i]), nameOf(list[j]));
        const mrnOneOff = mrnOneDigitApart(list[i].mrn, list[j].mrn);
        if (!nameMatch && !mrnOneOff) continue;
        links.push({
          a: i,
          b: j,
          rule: "same-dob",
          namePercent: nameMatch ? Math.max(1, confidenceFromNameMatch(nameMatch)) : 0,
          mrnOneOff,
        });
      }
    }
  }

  // A pair staff marked "Not a duplicate" is left out of the groups and kept
  // aside, so the list can show it under Hidden and offer "Show again". The
  // rest of a group still shows: a NEW record matching one of them reappears.
  for (const l of links) l.pair = pairKeyOf(list[l.a], list[l.b]);
  const isDismissed = (l) => !!l.pair && dismissed && dismissed.has(l.pair);
  const active = links.filter((l) => !isDismissed(l));
  const hidden = links.filter(isDismissed);

  return {
    groups: buildGroups(list, active),
    hiddenGroups: buildGroups(list, hidden),
    unreadableDob,
    blankDob,
  };
}

/** A record's identity across stores: "firestore:<id>" or "sheet:<token>". */
export const recordKey = (r) => (r && r.id ? `${r.source === "sheet" ? "sheet" : "firestore"}:${r.id}` : "");

/**
 * The saved id of a pair "Not a duplicate" decision: both record keys, sorted,
 * URI-encoded so a "/" in an id can't break the Firestore doc id. Holds record
 * ids only, never patient details. "" when either record has no id.
 */
export const pairKeyOf = (a, b) => {
  const x = recordKey(a);
  const y = recordKey(b);
  if (!x || !y) return "";
  return [x, y].sort().map(encodeURIComponent).join("__");
};

// Union-find: linked records form one group.
function buildGroups(list, links) {
  const parent = list.map((_, i) => i);
  const find = (x) => (parent[x] === x ? x : (parent[x] = find(parent[x])));
  for (const l of links) parent[find(l.a)] = find(l.b);

  const byRoot = new Map();
  for (const l of links) {
    const root = find(l.a);
    if (!byRoot.has(root)) byRoot.set(root, { members: new Set(), links: [] });
    const g = byRoot.get(root);
    g.members.add(l.a);
    g.members.add(l.b);
    g.links.push(l);
  }

  const groups = [...byRoot.values()].map((g) => {
    const members = [...g.members].sort((x, y) => x - y);
    const labelOf = new Map(members.map((m, n) => [m, letter(n)]));
    const out = members.map((m) => ({
      record: list[m],
      label: labelOf.get(m),
      why: g.links
        .filter((l) => l.a === m || l.b === m)
        .map((l) => `${describeLink(l)} as ${labelOf.get(l.a === m ? l.b : l.a)}`),
    }));
    return {
      records: out,
      links: g.links.map((l) => ({ ...l, a: labelOf.get(l.a), b: labelOf.get(l.b) })),
      best: Math.max(...g.links.map(rankOf)),
    };
  });

  groups.sort((x, y) => y.best - x.best || y.records.length - x.records.length);
  return groups;
}

/** The group's strongest reason, for its heading. */
export const groupHeadline = (group) => {
  const links = (group && group.links) || [];
  if (links.some((l) => l.rule === "same-mrn")) return "Same MRN";
  const best = Math.max(0, ...links.map((l) => l.namePercent || 0));
  if (best) return `Same DOB + name ${best}%`;
  return "Same DOB + MRN one digit off";
};

/** Filters for the on-screen list: does any link in the group meet this rule? */
export const GROUP_FILTERS = [
  { key: "all", label: "All" },
  { key: "same-mrn", label: "Same MRN" },
  { key: "name", label: "Same DOB + name" },
  { key: "mrn-one-off", label: "MRN one digit off" },
];
export const groupMatchesFilter = (group, key) => {
  const links = (group && group.links) || [];
  if (key === "same-mrn") return links.some((l) => l.rule === "same-mrn");
  if (key === "name") return links.some((l) => l.rule === "same-dob" && l.namePercent > 0);
  if (key === "mrn-one-off") return links.some((l) => l.rule === "same-dob" && l.mrnOneOff);
  return true;
};

// A, B, ... Z, AA, AB, ...
const letter = (n) => {
  let s = "";
  let k = n + 1;
  while (k > 0) {
    const r = (k - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    k = Math.floor((k - 1) / 26);
  }
  return s;
};
